import { useCallback, useMemo } from 'react';
import { useSharingList } from '@/hooks/sharing/useSharingList';
import { useAuth } from '@/context/AuthContext';
import { getUserDisplayName } from '@/utils/userDisplayUtils';

/** What the household calls itself in a record of its own work. */
export const SELF_ACTOR_NAME = 'You';

/**
 * Turns a `user_id` into a name, for the reads that identify people by number.
 *
 * The signed-in user is answered without consulting the list. They are the commonest actor by a
 * wide margin, the answer is better than their own name anyway, and a record of your own work
 * should read the same whether or not the sharing list has loaded.
 *
 * Returns `null` for an id it cannot place rather than inventing a label. That happens for real:
 * an id can belong to somebody whose access was withdrawn, or to a staff account that never
 * appeared in the list, and both are better shown as unattributed than as a guess.
 */
export const useHouseMemberNames = (houseId: number | undefined) => {
	const { currentUser } = useAuth();
	const { data: members } = useSharingList(houseId ?? 0);

	const namesById = useMemo(() => {
		const byId = new Map<number, string>();
		for (const member of members ?? []) {
			const name = getUserDisplayName(member);
			if (member.user_id != null && name) byId.set(member.user_id, name);
		}
		return byId;
	}, [members]);

	return useCallback(
		(userId: number | null | undefined): string | null => {
			if (userId == null) return null;
			if (currentUser?.id === userId) return SELF_ACTOR_NAME;

			return namesById.get(userId) ?? null;
		},
		[currentUser?.id, namesById],
	);
};
